import { useState } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { Eye, FileText } from "lucide-react";
import Button from "../../../components/Buttons/Button";
import ModalCreateInstantForm from "../../../components/Meta/ModalCreateInstantForm";
import MessageTemplatePreview from "../../../components/Meta/MessageTemplatePreview";
import ModalCreateLeadForm from "../../../components/LinkedIn/ModalCreateLeadForm";
import { useTranslationApp } from "../../../lib/i18next";
import {
  ADS_META_DESTINATION_TYPE_WEBSITE,
  CAMPAIGN_APPROVAL_DESTINATION_TITLE,
  CAMPAIGN_APPROVAL_LEAD_FORM_PREVIEW_UNAVAILABLE,
  FORM,
  INSTANT_FORM_PLACEHOLDER,
  MODAL_VIEW_LEAD_FORM_TITLE,
  WELCOME_MESSAGE_TEMPLATE_PLACEHOLDER,
} from "../../../i18n/keysTranslations";
import { META_DESTINATION_TYPE_TRANSLATION_KEYS } from "../../../utils/metaDestinationTypeLabels";
import {
  BORDER_RADIUS,
  LINKEDIN,
  META,
  TIKTOK,
} from "../../../utils/constants";
import {
  BORDER_GRAY_COLOR,
  GRAY_200,
  GRAY_50,
  GRAY_500,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";

const sectionTitleSx = {
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: "text.secondary",
  display: "block",
  mb: 1,
  textTransform: "uppercase",
};

const fieldLabelSx = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  display: "block",
};

const getDestinationLabelKey = ({ platform, destination }) => {
  if (destination?.leadForm) {
    return INSTANT_FORM_PLACEHOLDER;
  }

  if (platform === META) {
    return (
      META_DESTINATION_TYPE_TRANSLATION_KEYS[destination?.destinationType] ||
      ADS_META_DESTINATION_TYPE_WEBSITE
    );
  }

  return ADS_META_DESTINATION_TYPE_WEBSITE;
};

// Platforms whose lead forms open as the editor in read-only mode.
// TikTok uses the stored preview URL in a dialog, same as the campaign editor.
const LEAD_FORM_VIEW_PLATFORMS = [META, LINKEDIN];

// Where the ad sends people: website, lead form (with its read-only view on
// Meta and LinkedIn) or a messaging app with the welcome message template.
const ApprovalDestination = ({ platform, destination }) => {
  const { t } = useTranslationApp();
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const [isTikTokPreviewOpen, setIsTikTokPreviewOpen] = useState(false);
  const leadForm = destination?.leadForm;
  const leadFormValues = leadForm?.values;
  const supportsLeadFormView = LEAD_FORM_VIEW_PLATFORMS.includes(platform);
  const canOpenLeadForm = supportsLeadFormView && Boolean(leadFormValues);
  const tiktokPreviewUrl =
    platform === TIKTOK ? leadForm?.previewUrl || "" : "";

  return (
    <Box data-testid="campaign-approval-destination">
      <Typography variant="caption" sx={sectionTitleSx}>
        {t(CAMPAIGN_APPROVAL_DESTINATION_TITLE)}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography
            component="span"
            variant="body2"
            sx={{
              display: "inline-flex",
              px: 1.25,
              py: 0.5,
              borderRadius: 10,
              bgcolor: GRAY_50,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              fontWeight: 600,
            }}
          >
            {t(getDestinationLabelKey({ platform, destination }))}
          </Typography>
        </Box>

        {destination?.urls?.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {destination.urls.map((url, index) => (
              <Link
                key={`${url}-${index}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ overflowWrap: "anywhere" }}
              >
                {url}
              </Link>
            ))}
          </Box>
        ) : null}

        {leadForm ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              p: 1.5,
              bgcolor: GRAY_50,
              borderRadius: BORDER_RADIUS,
              border: `1px solid ${BORDER_GRAY_COLOR}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: GRAY_200,
                flexShrink: 0,
              }}
            >
              <FileText size={16} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography
                variant="caption"
                sx={{ ...fieldLabelSx, color: GRAY_500 }}
              >
                {t(FORM)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, overflowWrap: "anywhere" }}
              >
                {leadForm.name}
              </Typography>
              {supportsLeadFormView && !canOpenLeadForm ? (
                <Typography variant="caption" color="text.secondary">
                  {t(CAMPAIGN_APPROVAL_LEAD_FORM_PREVIEW_UNAVAILABLE)}
                </Typography>
              ) : null}
            </Box>
            {canOpenLeadForm || tiktokPreviewUrl ? (
              <Button
                type="button"
                color="secondary"
                startIcon={<Eye size={16} />}
                onClick={() =>
                  tiktokPreviewUrl
                    ? setIsTikTokPreviewOpen(true)
                    : setIsLeadFormOpen(true)
                }
                sx={{ minWidth: "max-content", height: 36, px: 2 }}
              >
                {t(MODAL_VIEW_LEAD_FORM_TITLE)}
              </Button>
            ) : null}
          </Box>
        ) : null}

        {destination?.messageTemplate ? (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ...fieldLabelSx, mb: 1 }}
            >
              {t(WELCOME_MESSAGE_TEMPLATE_PLACEHOLDER)}
            </Typography>
            <MessageTemplatePreview value={destination.messageTemplate} />
          </Box>
        ) : null}
      </Box>

      {isLeadFormOpen && platform === META ? (
        <ModalCreateInstantForm
          modalOpen
          onCloseModal={() => setIsLeadFormOpen(false)}
          initialValues={leadFormValues}
          readOnly
        />
      ) : null}
      {isLeadFormOpen && platform === LINKEDIN ? (
        <ModalCreateLeadForm
          modalOpen
          onCloseModal={() => setIsLeadFormOpen(false)}
          initialValues={leadFormValues}
          readOnly
        />
      ) : null}
      <Dialog
        open={isTikTokPreviewOpen && Boolean(tiktokPreviewUrl)}
        onClose={() => setIsTikTokPreviewOpen(false)}
        aria-label={t(MODAL_VIEW_LEAD_FORM_TITLE)}
        PaperProps={{
          sx: {
            backgroundColor: "#FFFFFF",
            borderRadius: 3,
            border: "1px solid #E5E5E5",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
            overflow: "hidden",
            m: 2,
            width: 390,
            maxWidth: "92vw",
          },
        }}
      >
        {tiktokPreviewUrl ? (
          <Box
            component="iframe"
            src={tiktokPreviewUrl}
            title={t(MODAL_VIEW_LEAD_FORM_TITLE)}
            sx={{
              display: "block",
              width: "100%",
              height: "min(720px, 80vh)",
              border: 0,
            }}
          />
        ) : null}
      </Dialog>
    </Box>
  );
};

export default ApprovalDestination;
